import { useState } from 'react';
import { cn } from '@/lib/utils';
import { AgingPeriod, getClientesByPeriodo } from '@/data/dashboardMockData';
import { ClientListModal } from './ClientListModal';

interface AgingTableProps {
  type: 'open' | 'overdue';
  data: AgingPeriod[];
  showTotal?: boolean;
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function AgingTable({ type, data, showTotal = false }: AgingTableProps) {
  const [selectedPeriodo, setSelectedPeriodo] = useState<string | null>(null);
  
  const title = type === 'open' ? 'Contas a Receber em Aberto' : 'Contas a Receber Vencidas';
  const total = data.reduce((sum, p) => sum + p.valor, 0);
  const totalClientes = data.reduce((sum, p) => sum + p.clientes, 0);
  const totalFaturas = data.reduce((sum, p) => sum + p.faturas, 0);
  
  const headerBg = type === 'open' 
    ? 'bg-[hsl(var(--basic-50))]' 
    : 'bg-[hsl(var(--careful-50))]';
  
  const headerBorder = type === 'open'
    ? 'border-[hsl(var(--basic-100))]'
    : 'border-[hsl(var(--careful-100))]';

  const handleRowClick = (periodoLabel: string) => {
    setSelectedPeriodo(periodoLabel);
  };

  const clients = selectedPeriodo 
    ? getClientesByPeriodo(type, selectedPeriodo) 
    : [];

  return (
    <>
      <div className="rounded-2xl border bg-card overflow-hidden hover:shadow-lg transition-all duration-300">
        <div className={cn('px-4 py-3 border-b', headerBg, headerBorder)}>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground">
            Total: {formatMoney(total)}
          </p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 px-4 font-semibold">Período</th>
                <th className="text-center py-2 px-2 font-semibold">Clientes</th>
                <th className="text-center py-2 px-2 font-semibold">Faturas</th>
                <th className="text-right py-2 px-2 font-semibold">Valor</th>
                <th className="text-right py-2 px-4 font-semibold">%</th>
              </tr>
            </thead>
            <tbody>
              {data.map((period, index) => (
                <tr 
                  key={period.label}
                  onClick={() => handleRowClick(period.label)}
                  className={cn(
                    'border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer',
                    index === data.length - 1 && !showTotal && 'border-b-0'
                  )}
                >
                  <td className="py-2.5 px-4">
                    <span className="text-foreground font-medium text-xs hover:underline">
                      {period.label}
                    </span>
                  </td>
                  <td className="py-2.5 px-2 text-center">
                    <span className="text-foreground text-xs">{period.clientes}</span>
                  </td>
                  <td className="py-2.5 px-2 text-center">
                    <span className="text-foreground text-xs">{period.faturas}</span>
                  </td>
                  <td className="py-2.5 px-2 text-right">
                    <span className="text-foreground font-medium text-xs">
                      {formatMoney(period.valor)}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={cn(
                            'h-full rounded-full',
                            type === 'open' 
                              ? 'bg-[hsl(var(--basic-500))]' 
                              : 'bg-[hsl(var(--careful-500))]'
                          )}
                          style={{ width: `${period.percentual}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground w-8 text-right">
                        {period.percentual}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
              
              {/* Linha de Total */}
              {showTotal && (
                <tr className={cn('font-semibold', headerBg)}>
                  <td className="py-2.5 px-4">
                    <span className="text-foreground text-xs uppercase">Total</span>
                  </td>
                  <td className="py-2.5 px-2 text-center">
                    <span className="text-foreground text-xs">{totalClientes}</span>
                  </td>
                  <td className="py-2.5 px-2 text-center">
                    <span className="text-foreground text-xs">{totalFaturas}</span>
                  </td>
                  <td className="py-2.5 px-2 text-right">
                    <span className={cn(
                      'font-bold text-xs',
                      type === 'overdue' ? 'text-[hsl(var(--careful-600))]' : 'text-foreground'
                    )}>
                      {formatMoney(total)}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    <span className="text-xs text-muted-foreground">100%</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ClientListModal
        isOpen={selectedPeriodo !== null}
        onClose={() => setSelectedPeriodo(null)}
        title={selectedPeriodo || ''}
        clients={clients}
        type={type}
      />
    </>
  );
}
